import React from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  App,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PageHeader from "../../../components/PageHeader";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { useAuth } from "../../../hooks/useAuth";
import {
  AdminAccessEntry,
  useGetAdminAccessQuery,
  useGrantAdminAccessMutation,
  useRevokeAdminAccessMutation,
} from "../../../store/api/adminAccessApi";

const { Text } = Typography;

const GENERIC_ERROR = "No se pudo completar la acción. Intenta nuevamente.";

/** Reads the backend's message out of an RTK Query error, if it has one. */
const errorMessage = (error: unknown): string => {
  const data = (error as { data?: { message?: string | string[] } } | undefined)
    ?.data;
  const message = data?.message;

  if (Array.isArray(message)) {
    return message[0] ?? GENERIC_ERROR;
  }

  return message || GENERIC_ERROR;
};

const AdminAccess: React.FC = () => {
  const { notification } = App.useApp();
  const { user } = useAuth();
  const [form] = Form.useForm<{ email: string }>();
  const {
    data: entries,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAdminAccessQuery();
  const [grantAccess, { isLoading: isGranting }] = useGrantAdminAccessMutation();
  const [revokeAccess] = useRevokeAdminAccessMutation();

  const onGrant = async (values: { email: string }) => {
    try {
      await grantAccess({ email: values.email.trim() }).unwrap();
      form.resetFields();
      notification.success({
        message: "Acceso otorgado",
        description: `${values.email.trim()} ya puede entrar a Admin Emmi con su cuenta de Google.`,
      });
    } catch (error) {
      notification.error({
        message: "No se pudo otorgar el acceso",
        description: errorMessage(error),
      });
    }
  };

  const onRevoke = async (entry: AdminAccessEntry) => {
    try {
      await revokeAccess(entry.id).unwrap();
      notification.success({
        message: "Acceso eliminado",
        description: `${entry.email ?? "El usuario"} ya no puede entrar a Admin Emmi.`,
      });
    } catch (error) {
      notification.error({
        message: "No se pudo eliminar el acceso",
        description: errorMessage(error),
      });
    }
  };

  const columns = [
    {
      title: "Correo",
      dataIndex: "email",
      key: "email",
      render: (email: string | null, entry: AdminAccessEntry) => (
        <Space>
          <Text>{email ?? "—"}</Text>
          {entry.id === user?.id && <Tag>Tú</Tag>}
        </Space>
      ),
    },
    {
      title: "Nombre",
      key: "name",
      render: (_: unknown, entry: AdminAccessEntry) =>
        `${entry.first_name} ${entry.last_name}`.trim(),
    },
    {
      title: "Tipo",
      dataIndex: "is_super_admin",
      key: "is_super_admin",
      render: (isSuperAdmin: boolean) =>
        isSuperAdmin ? (
          <Tag color="purple">Administrador principal</Tag>
        ) : (
          <Tag>Administrador</Tag>
        ),
    },
    {
      title: "Con acceso desde",
      dataIndex: "created_at",
      key: "created_at",
      render: (createdAt: string) => dayjs(createdAt).format("DD/MM/YYYY"),
    },
    {
      title: "Acciones",
      key: "actions",
      align: "right" as const,
      render: (_: unknown, entry: AdminAccessEntry) => (
        <Popconfirm
          title="Eliminar acceso"
          description={`${entry.email ?? "Este usuario"} dejará de entrar a Admin Emmi de inmediato.`}
          okText="Eliminar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true }}
          onConfirm={() => onRevoke(entry)}
        >
          <Button danger type="text" icon={<DeleteOutlined />}>
            Eliminar
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (isLoading) {
    return <ActivityIndicator />;
  }

  return (
    <>
      <PageHeader
        title="Accesos"
        extra={
          <Form
            form={form}
            layout="inline"
            onFinish={onGrant}
            requiredMark={false}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Ingresa un correo" },
                { type: "email", message: "Ingresa un correo válido" },
              ]}
            >
              <Input
                placeholder="correo@emmi.com.mx"
                style={{ minWidth: 260 }}
                allowClear
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={isGranting}
              >
                Agregar acceso
              </Button>
            </Form.Item>
          </Form>
        }
      />

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la lista de accesos."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Card>
          <Text type="secondary">
            Solo los correos de esta lista pueden entrar a Admin Emmi con su
            cuenta de Google. Los cambios se aplican de inmediato.
          </Text>
          <Table
            style={{ marginTop: 16 }}
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={entries ?? []}
            loading={isFetching}
            pagination={false}
            locale={{ emptyText: "Todavía no hay accesos configurados" }}
          />
        </Card>
      )}
    </>
  );
};

export default AdminAccess;
